import {
  MultiComboBox,
  MultiComboItem,
  MultiComboBoxProps,
} from '@florencelabs/multi-combo-box';
import { useSearchParams } from 'next/navigation';
import {
  useState,
  ReactElement,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'react-stately';

import { DrawerAwareMobileMultiComboBox } from '@provider-directory/components/drawer-aware-mobile-combobox/drawer-aware-mobile-multicombobox';
import {
  FilterDrawerFooter,
  useSearchFilters,
} from '@provider-directory/contexts/SearchFiltersContext';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { FilterOption } from '@provider-directory/types/search.model';

interface LanguagesFilterProps
  extends Omit<
    MultiComboBoxProps<FilterOption>,
    'children' | 'onSelectionChange' | 'items'
  > {
  items: FilterOption[];
  onSelectionChange: (language: Set<Key> | Key | null) => void;
  /** URL query param for selected values (default: languages) */
  urlParam?: string;
}

export const LanguagesFilter = ({
  items,
  onSelectionChange,
  urlParam = 'languages',
  ...restProps
}: LanguagesFilterProps): ReactElement => {
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { registerOnShowResults } = useSearchFilters();

  // Labels may be i18n keys (e.g. searchFilters.dayMon) or plain facet values
  // (e.g. language names). t() returns the key unchanged when missing.
  const translatedItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        label: t(item.label),
      })),
    [items, t],
  );

  const validKeys = useMemo(
    () => new Set<Key>(translatedItems.map((item) => item.key)),
    [translatedItems],
  );

  const keysFromUrl = useMemo(() => {
    const terms = searchParams.getAll(urlParam).map((term) => term.trim());
    const keys = new Set<Key>();
    for (const term of terms) {
      if (!term) continue;
      // Ignore values that don't match a known option so the selected state
      // mirrors the search request, which only applies recognised languages.
      if (!validKeys.has(term)) continue;
      keys.add(term);
    }
    return keys;
  }, [searchParams, validKeys, urlParam]);

  const [selectedKeys, setSelectedKeys] = useState(keysFromUrl);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const selectedKeysRef = useRef(keysFromUrl);
  const autoSelectRef = useRef<
    ((e?: React.FocusEvent | React.KeyboardEvent) => void) | null
  >(null);
  const trayInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    selectedKeysRef.current = keysFromUrl;
    setSelectedKeys(keysFromUrl);
  }, [keysFromUrl]);

  useEffect(() => {
    if (isDesktop) return;
    if (!registerOnShowResults) return;
    return registerOnShowResults(() => {
      onSelectionChange(selectedKeysRef.current);
    });
  }, [isDesktop, onSelectionChange, registerOnShowResults]);

  const getVisibleMobileOptionCount = useCallback((): number | null => {
    if (typeof document === 'undefined') return null;

    const isVisible = (el: Element) => {
      const node = el as HTMLElement;
      if (node.getAttribute('aria-hidden') === 'true') return false;
      if ((node as any).hidden) return false;
      const rects = node.getClientRects();
      return rects.length > 0 && rects[0].width > 0 && rects[0].height > 0;
    };

    const tray = trayInputRef.current?.closest('[data-testid$="-tray"]');
    if (!tray) return null;

    const selector = '[role="option"]:not([aria-disabled="true"])';
    const els = Array.from(tray.querySelectorAll(selector)).filter(isVisible);
    if (els.length === 0) return null;
    return els.length;
  }, []);

  const shouldAllowAutoSelect = useCallback((): boolean => {
    return getVisibleMobileOptionCount() === 1;
  }, [getVisibleMobileOptionCount]);

  const handleMobileClose = useCallback(() => {
    onSelectionChange(selectedKeysRef.current);
  }, [onSelectionChange]);

  const handleTrayKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const isSubmitKey = event.key === 'Enter' || event.key === 'Tab';
      if (!isSubmitKey) return;

      event.preventDefault();
      event.stopPropagation();

      if (shouldAllowAutoSelect()) {
        autoSelectRef.current?.(event);
        return;
      }

      setIsMobileOpen(false);
      handleMobileClose();
    },
    [shouldAllowAutoSelect, handleMobileClose],
  );

  const onAutoSelect = (key: Key) => {
    if (!isDesktop && !shouldAllowAutoSelect()) return;

    const selectedOption = translatedItems.find((i) => i.key === key);
    if (!selectedOption || selectedOption.isDisabled) return;

    const next = new Set<Key>(selectedKeysRef.current);
    if (next.has(key)) return;

    next.add(key);
    selectedKeysRef.current = next;
    setSelectedKeys(next);

    if (isDesktop) {
      onSelectionChange(next);
    }
  };

  const commonProps = {
    ...restProps,
    onAutoSelect,
    selectedKeys,
    onSelectionChange: (keys: Set<Key> | null) => {
      const nextKeys = keys ?? new Set<Key>();
      selectedKeysRef.current = nextKeys;
      setSelectedKeys(nextKeys);
    },
  };

  const onClear = () => {
    const emptyKeys = new Set<Key>();
    selectedKeysRef.current = emptyKeys;
    setSelectedKeys(emptyKeys);
    onSelectionChange(emptyKeys);
  };

  const renderComboItems = (isMobile: boolean) =>
    translatedItems.map((option) => (
      <MultiComboItem
        data-testid={`language-filter-option-${option.key}`}
        isMobile={isMobile}
        id={option.key}
        key={option.key}
        value={option}
        textValue={option.label}
        className={
          isMobile && selectedKeys.has(option.key)
            ? 'text-primary-color'
            : undefined
        }
      >
        <span>{option.label}</span>
        <span
          className={
            'provider-search-filter-option-count ml-2 text-neutral-500'
          }
        >
          {option.count}
        </span>
      </MultiComboItem>
    ));

  const containerRef = useRef<HTMLDivElement>(null);

  return isDesktop ? (
    <div
      ref={containerRef}
      style={{ outline: 'none', display: 'inline-block' }}
    >
      <MultiComboBox
        {...commonProps}
        testIdPrefix={'search-filter'}
        onClose={() => {
          if (containerRef.current) {
            containerRef.current.focus({ preventScroll: true });
          }
          if (selectedKeys === keysFromUrl) return;
          window.requestAnimationFrame(() => {
            onSelectionChange(selectedKeys);
          });
        }}
        onClear={onClear}
      >
        {renderComboItems(false)}
      </MultiComboBox>
    </div>
  ) : (
    <DrawerAwareMobileMultiComboBox
      {...commonProps}
      testIdPrefix={'mobile-languages-filter'}
      autoSelectRef={autoSelectRef}
      autoFocusOnOpen={false}
      trayInputRef={trayInputRef}
      onTrayKeyDown={handleTrayKeyDown}
      onClose={handleMobileClose}
      isOpen={isMobileOpen}
      onOpenChange={setIsMobileOpen}
      onClear={onClear}
      placeholder={t('search.submit')}
      popoverFooter={
        <FilterDrawerFooter
          isClearDisabled={selectedKeys.size === 0}
          onClear={onClear}
          resultsText={'searchFilters.showResults'}
          onShowResults={() => {
            if (shouldAllowAutoSelect()) {
              autoSelectRef.current?.();
            }
          }}
        />
      }
    >
      {renderComboItems(true)}
    </DrawerAwareMobileMultiComboBox>
  );
};
